/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { styled, Box } from '@mui/material';
import Iconify from '../../Iconify';
import Downloading from '../../custom/Loading/Downloading';

const CustLink = styled('span')(({ theme }) => ({
  display: 'block',
  color: theme.palette.text.primary,
  fontWeight: 'bold',
  padding: '4px 8px',
  cursor: 'pointer',
  margin: '6px 2px',
  border: 'none',
  borderRadius: '5px',
  background: theme.palette.background.paper,
  boxShadow: `0px 0px 1px ${theme.palette.invertedBackground.default}`,
  transition: '0.2s',
  '& svg': {
    position: 'relative',
    top: '2px',
    marginRight: '8px',
  },
  '& span': {
    display: 'inline-block',
    textAlign: 'right',
    marginRight: '16px',
  },
  '& .size': {
    width: '70px',
  },
  '& .type': {
    width: '110px',
    textAlign: 'center',
  },
  '&:hover': {
    color: theme.palette.text.secondary,
    background: theme.palette.background.default,
  },
}));

DigitalDownload.propTypes = {
  object: PropTypes.any,
};

export default function DigitalDownload({ object }) {
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);
  const size = useCallback((size) => {
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${Math.round(size / 1024)} kB`;
    }
    if (size < 1024 * 1024 * 1024) {
      return `${Math.round(size / 1024 / 1024)} MB`;
    }
    if (size < 1024 * 1024 * 1024 * 1024) {
      return `${Math.round(size / 1024 / 1024 / 1024)} GB`;
    }
    return `${Math.round(size / 1024 / 1024 / 1024 / 1024)} TB`;
  }, []);

  function forceDownload(blob, filename) {
    const a = document.createElement('a');
    a.download = filename;
    a.href = blob;
    // For Firefox https://stackoverflow.com/a/32226068
    document.body.appendChild(a);
    a.click();
    a.remove();
    setLoading(false);
  }

  function downloadResource(url, filename) {
    setLoading(true);
    if (!filename) filename = url.split('\\').pop().split('/').pop();
    fetch(url, {
      headers: new Headers({
        // eslint-disable-next-line no-restricted-globals
        Origin: location.origin,
      }),
      mode: 'cors',
    })
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        forceDownload(blobUrl, filename);
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
      });
  }

  useEffect(() => {
    let tempOutput = null;
    switch (object?.type) {
      case 'VIDEO':
        tempOutput = (
          <div>
            {object.videos.map((file, idx) => (
              <CustLink
                key={idx}
                onClick={(e) => {
                  e.preventDefault();
                  downloadResource(file.fileUrl);
                }}
              >
                <Iconify icon="fa-regular:play-circle" rel="noopener noreferrer" />
                <span className="size">{size(file.fileSize)}</span>
                <span className="type">{file.mimeType}</span>
                {object.label || file.label || `VIDEO_${idx + 1}`}
              </CustLink>
            ))}
          </div>
        );
        break;
      case 'AUDIO':
        tempOutput = (
          <div>
            {object.audioFiles.map((file, idx) => (
              <CustLink
                key={idx}
                onClick={(e) => {
                  e.preventDefault();
                  downloadResource(file.fileUrl);
                }}
              >
                <Iconify icon="fa:music" target="_blank" rel="noopener noreferrer" />
                <span className="size">{size(file.fileSize)}</span>
                <span className="type">{file.mimeType}</span>
                {object.label || file.label || `AUDIO_${idx + 1}`}
              </CustLink>
            ))}
          </div>
        );
        break;
      case 'IMAGES':
      case 'images':
        tempOutput = (
          <div>
            {object.images.map((file, idx) => (
              <CustLink
                key={idx}
                onClick={(e) => {
                  e.preventDefault();
                  downloadResource(file.full.fileUrl);
                }}
                href={file.full.fileUrl}
                download
              >
                <Iconify icon="fa-regular:image" target="_blank" rel="noopener noreferrer" />
                <span className="size">{size(file.full.fileSize)}</span>
                <span className="type">{file.full.mimeType}</span>
                {object.label || file.label || file.full.label || `IMAGE_${idx + 1}`}
              </CustLink>
            ))}
          </div>
        );
        break;
      case 'OBJECT_PANORAMA':
        tempOutput = (
          <div>
            <CustLink
              onClick={(e) => {
                e.preventDefault();
                downloadResource(object?.tile?.fileUrl);
              }}
            >
              <Iconify icon="solar:panorama-bold" target="_blank" rel="noopener noreferrer" />
              <span className="size">{size(object?.tile?.fileSize)}</span>
              <span className="type">{object?.tile?.mimeType}</span>
              {object?.label}
            </CustLink>
          </div>
        );

        break;
      case 'BOOK':
        tempOutput = (
          <div>
            {object.ebook?.map((file, idx) => (
              <CustLink
                key={idx}
                onClick={(e) => {
                  e.preventDefault();
                  downloadResource(file.fileUrl);
                }}
              >
                <Iconify icon="material-symbols:menu-book-outline" target="_blank" rel="noopener noreferrer" download />
                <span className="size">{size(file.fileSize)}</span>
                <span className="type">{file.mimeType?.replace('application/', '')}</span>
                {object.label || file.label || `BOOK_${idx + 1}`}
              </CustLink>
            ))}
            {object.pages.map((file, idx) => (
              <CustLink
                key={idx}
                onClick={(e) => {
                  e.preventDefault();
                  downloadResource(file.full.fileUrl);
                }}
              >
                <Iconify icon="material-symbols:menu-book-outline" target="_blank" rel="noopener noreferrer" />
                <span className="size">{size(file.full.fileSize)}</span>
                <span className="type">{file.full.mimeType}</span>({idx + 1}/{object.pages.length}){' '}
                {object.label || file.label || file.full.label || `BOOK_${idx + 1}`}
              </CustLink>
            ))}
          </div>
        );
        break;
      case '3D_MODEL':
        tempOutput = (
          <div>
            <CustLink
              onClick={(e) => {
                e.preventDefault();
                downloadResource(object.model.fileUrl);
              }}
            >
              <Iconify icon="material-symbols:3d-rotation" target="_blank" rel="noopener noreferrer" />
              <span className="size">{size(object.model.fileSize)}</span>
              <span className="type">{object.model.mimeType}</span>
              {object.label}
            </CustLink>
          </div>
        );
        break;
      case 'DOWNLOAD':
        tempOutput = (
          <div>
            {object.files.map((file, idx) => (
              <CustLink
                key={idx}
                className="dasda"
                onClick={(e) => {
                  e.preventDefault();
                  downloadResource(file.fileUrl);
                }}
              >
                <Iconify icon="ic:outline-cloud-download" target="_blank" rel="noopener noreferrer" />
                <span className="size">{size(file.fileSize)}</span>
                <span className="type">{file.mimeType}</span>
                {file.label}
              </CustLink>
            ))}
          </div>
        );
        break;
      default:
        tempOutput = '';
    }
    setOutput(tempOutput);
  }, [object, size]);

  return (
    <>
      {!loading ? (
        output
      ) : (
        <Box p={3}>
          <Downloading />
        </Box>
      )}
    </>
  );
}
